import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    CircularProgress,
    Typography,
    Chip,
    Rating,
    Button,
    InputAdornment,
    Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { get } from '../../../components/hooks/apiFetch';
import './PromptSelectionModal.css'

// Маппинг статусов
const STATUS_MAP = {
    0: { label: 'Стандартный', color: 'default' },
    1: { label: 'Тестовый', color: 'warning' },
    2: { label: 'Черновой', color: 'error' },
    3: { label: 'Продакшен', color: 'success' }
};

// Компонент для рейтинга
const CustomRating = ({ value, readOnly = true, size = 'small' }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Rating
            value={value}
            readOnly={readOnly}
            precision={0.5}
            size={size}
            sx={{
                '& .MuiRating-iconFilled': {
                    color: '#ff9800',
                }
            }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#666' }}>
            {value?.toFixed(1) || '0.0'}
        </Typography>
    </Box>
);

// Компонент для отображения статуса
const StatusBadge = ({ status }) => {
    const statusInfo = STATUS_MAP[status] || STATUS_MAP[0];
    return (
        <Chip
            variant="outlined"
            className="chip-role"
            label={statusInfo.label}
            size="small"
        />
    );
};

export const PromptSelectionModal = ({ open, onClose, onSelect, selectedSource }) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState([]);

    // Загрузка промптов
    const fetchOptions = async () => {
        setLoading(true);
        try {
            const data = await get('/api/Prompt');
            setOptions(data);
            setFilteredOptions(data);
        } catch (error) {
            console.error('Error fetching options:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchOptions();
        }
    }, [open]);

    // Фильтрация при поиске
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredOptions(options);
        } else {
            const filtered = options.filter(option =>
                option.name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredOptions(filtered);
        }
    }, [searchTerm, options]);

    const handleSelect = (option) => {
        onSelect(option);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '80vh'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0',
                pb: 2
            }}>
                <Typography variant="h6" fontWeight="600">
                    Выберите промпт
                </Typography>
                <Button
                    onClick={onClose}
                    sx={{ minWidth: 'auto', p: 0.5 }}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <div className="mt-2 mb-2">
                    <TextField
                        fullWidth
                        variant="standard"
                        placeholder="Поиск по названию..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input text-input"
                        autocomplete="off"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon className="search-icon" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>

                {/* Список промптов */}
                <Box sx={{
                    maxHeight: '400px', overflow: 'auto'
                }}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {/* Список существующих промптов */}
                            {filteredOptions.length === 0 ? (
                                <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                                    Промпты не найдены
                                </Typography>
                            ) : (
                                filteredOptions.map((option) => (
                                    <Tooltip title={selectedSource?.guid === option.guid && "Этот промпт уже выбран"}>
                                        <Box
                                            key={option.guid}
                                            onClick={() => handleSelect(option)}
                                            className={`prompt-item p-3 mb-2 ${selectedSource?.guid === option.guid ? "selected-prompt-item" : "unselected-prompt-item"}`}
                                        >
                                            <Typography sx={{ fontWeight: 600, color: 'var(--DT1520-Gray) !important' }} variant="subtitle1" gutterBottom>
                                                {option.name}
                                            </Typography>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                                <CustomRating value={option.rating || 0} />
                                                <StatusBadge className="status-badge" status={option.status || 0} />
                                                {option.model !== undefined && option.model !== 0 && (
                                                    <Chip
                                                        label={`Модель: ${option.model === 1 ? '4B' : option.model === 2 ? '30B' : option.model === 3 ? '14B' : 'None'}`}
                                                        variant="outlined"
                                                        className="chip-role"
                                                        size="small"
                                                    />
                                                )}
                                            </Box>

                                            {option.description && (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{
                                                        mt: 1,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical'
                                                    }}
                                                >
                                                    {option.description}
                                                </Typography>
                                            )}

                                            <Box sx={{ display: 'flex', gap: 2, mt: 1, fontSize: '0.75rem', color: '#999' }}>
                                                <span>ID: {option.id}</span>
                                                {option.creatorLogin && <span>Автор: {option.creatorLogin}</span>}
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                ))
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} className="dt1520-button active-dt1520-button view-button default-button">
                    Отмена
                </Button>
            </DialogActions>
        </Dialog>
    );
};